import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { isUserAdminSelector, isUserOrganisationSelector } from "redux/selectors/user";
import {
    getStoredImpersonationState,
    IMPERSONATION_STATE_CHANGED_EVENT,
    isImpersonationEnabled,
    startImpersonation,
    stopImpersonation,
} from "services/impersonation";

const useImpersonation = () => {
    const [t] = useTranslation("common");
    const isAdmin = useSelector(isUserAdminSelector);
    const isOrganisation = useSelector(isUserOrganisationSelector);
    const canAccessImpersonation = isAdmin || isOrganisation;
    const [impersonationState, setImpersonationState] = useState(() => getStoredImpersonationState());

    useEffect(() => {
        const syncState = () => {
            const nextState = getStoredImpersonationState();
            setImpersonationState(nextState);
        };

        syncState();

        window.addEventListener("storage", syncState);
        window.addEventListener(IMPERSONATION_STATE_CHANGED_EVENT, syncState);

        return () => {
            window.removeEventListener("storage", syncState);
            window.removeEventListener(IMPERSONATION_STATE_CHANGED_EVENT, syncState);
        };
    }, []);

    const enabled = isImpersonationEnabled();
    const isImpersonating = Boolean(impersonationState?.impersonationId);

    const canStartImpersonation = useMemo(() => {
        return Boolean(enabled && canAccessImpersonation && !isImpersonating);
    }, [canAccessImpersonation, enabled, isImpersonating]);

    const start = useCallback(
        async ({ email, reason }) => {
            try {
                const state = await startImpersonation({ email, reason });
                toast.success(t("admin.impersonation_started"));
                return state;
            } catch (error) {
                toast.error(error?.message || t("admin.impersonation_start_failed"));
                return null;
            }
        },
        [t]
    );

    const stop = useCallback(async () => {
        try {
            await stopImpersonation();
            toast.success(t("admin.impersonation_stopped"));
            return true;
        } catch (error) {
            toast.error(error?.message || t("admin.impersonation_stop_failed"));
            return false;
        }
    }, [t]);

    return {
        enabled,
        isAdmin,
        isOrganisation,
        canAccessImpersonation,
        isImpersonating,
        impersonationState,
        canStartImpersonation,
        start,
        stop,
    };
};

const impersonationHooks = {
    useImpersonation,
};

export default impersonationHooks;
